import { useEffect, useRef, useState } from "react";

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        options: {
          sitekey: string;
          theme?: "light" | "dark" | "auto";
          callback: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: (errorCode?: number) => boolean | void;
          retry?: "auto" | "never";
          "retry-interval"?: number;
        }
      ) => string;
      reset?: (widgetId?: string) => void;
    };
  }
}

type TurnstileWidgetProps = {
  siteKey: string;
  theme?: "light" | "dark" | "auto";
  onVerify: (token: string) => void;
  onExpire?: () => void;
  onError?: (errorCode?: number) => void;
};

/**
 * Cloudflare Turnstile widget. Script must be loaded in index.html:
 * <script src="https://challenges.cloudflare.com/turnstile/v0/api.js" async defer></script>
 * CSP must allow script-src and frame-src for https://challenges.cloudflare.com (index.html meta + netlify.toml).
 * Renders once when script is ready; polls until window.turnstile and container ref are available.
 */
export function TurnstileWidget({
  siteKey,
  theme = "dark",
  onVerify,
  onExpire,
  onError,
}: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const renderedRef = useRef(false);
  const [scriptFailed, setScriptFailed] = useState(false);
  const onVerifyRef = useRef(onVerify);
  const onExpireRef = useRef(onExpire);
  const onErrorRef = useRef(onError);
  onVerifyRef.current = onVerify;
  onExpireRef.current = onExpire;
  onErrorRef.current = onError;

  useEffect(() => {
    if (!siteKey) return;

    const timeout = window.setTimeout(() => {
      if (!renderedRef.current && !window.turnstile) setScriptFailed(true);
    }, 5000);

    const tryRender = () => {
      if (renderedRef.current || !containerRef.current) return;
      if (!window.turnstile) return;
      renderedRef.current = true;
      setScriptFailed(false);
      window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        theme,
        callback: (token) => onVerifyRef.current(token),
        "expired-callback": () => onExpireRef.current?.(),
        "error-callback": (errorCode) => {
          onErrorRef.current?.(errorCode);
          return true;
        },
        retry: "auto",
        "retry-interval": 8000,
      });
    };

    tryRender();
    const interval = setInterval(tryRender, 100);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [siteKey, theme]);

  return (
    <div className="turnstile-widget-wrapper" style={{ minHeight: 65 }}>
      <div ref={containerRef} />
      {scriptFailed && (
        <p className="text-sm mt-2" style={{ color: "#8B5C58" }}>
          Verification could not load. Check that the Turnstile script is allowed (CSP: script-src and frame-src for challenges.cloudflare.com) and try refreshing.
        </p>
      )}
    </div>
  );
}
